import type { ReactNode } from 'react';

import classNames from 'classnames';

import { MOBILE_MEDIA_QUERY } from '../../../../config/constants';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { WIDGET_SMALL_EXTENDED_WIDTH, WIDGET_SMALL_WIDTH, WIDGET_WIDE_WIDTH } from '../../constants';
import type { WidgetSizeType } from '../../types';

import styles from './WidgetWrapper.module.scss';

interface WidgetWrapperProps {
  sizeType: WidgetSizeType;
  children: ReactNode;
  smallWidgetExtended?: boolean;
  onClick?: () => void;
  className?: string;
  id?: string;
  dataTestId?: string;
  dataProvider?: string;
  dataBonusCode?: string;
  // TEMP: lets stamp card's open animation grow wider than the fixed width.
  widthOverride?: { mobile: number; desktop: number };
  // TEMP: covers the whole open/close sequence, including the shrink-back after widthOverride
  // clears — scopes the width transition to stamp card instead of every widget.
  animateWidth?: boolean;
  // TEMP: lets stamp card's close pop bleed past the wrapper's overflow: hidden.
  overflowVisible?: boolean;
}

const getContainerClassName = (type: WidgetSizeType, smallWidgetExtended?: boolean): string => {
  if (type === 'small') {
    if (smallWidgetExtended) {
      return styles.containerSmallExtended;
    }
    return styles.containerSmall;
  }
  return styles.containerWide;
};

const getWidth = (
  type: WidgetSizeType,
  smallWidgetExtended?: boolean,
  isMobile?: boolean,
  widthOverride?: { mobile: number; desktop: number },
): number => {
  if (widthOverride) {
    return isMobile ? widthOverride.mobile : widthOverride.desktop;
  }
  if (type === 'small') {
    if (smallWidgetExtended) {
      return isMobile ? WIDGET_SMALL_EXTENDED_WIDTH.mobile : WIDGET_SMALL_EXTENDED_WIDTH.desktop;
    }
    return isMobile ? WIDGET_SMALL_WIDTH.mobile : WIDGET_SMALL_WIDTH.desktop;
  }
  return isMobile ? WIDGET_WIDE_WIDTH.mobile : WIDGET_WIDE_WIDTH.desktop;
};

export function WidgetWrapper(props: WidgetWrapperProps) {
  const {
    sizeType,
    smallWidgetExtended,
    onClick,
    children,
    className,
    id,
    dataTestId,
    dataProvider,
    dataBonusCode,
    widthOverride,
    animateWidth,
    overflowVisible,
  } = props;
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
  const containerClassName = getContainerClassName(sizeType, smallWidgetExtended);
  const width = getWidth(sizeType, smallWidgetExtended, isMobile, widthOverride);

  return (
    <div
      className={classNames(
        styles.container,
        containerClassName,
        animateWidth && styles.containerWidthAnimated,
        overflowVisible && styles.containerOverflowVisible,
        className,
      )}
      data-bonus-code={dataBonusCode}
      data-provider={dataProvider}
      data-testid={dataTestId}
      id={id}
      style={{ width, minWidth: width, maxWidth: width }}
      onClick={onClick}
    >
      {children}
    </div>
  );
}
